import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Activity, Loader2, Eye, Clock, Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKET_KEYS = ['0', '10', '20', '30', '40', '50', '60', '70', '80', '90'];

function BucketsBar({ buckets }) {
  const max = Math.max(1, ...BUCKET_KEYS.map(k => buckets[k] || 0));
  return (
    <div className="flex items-end gap-0.5 h-12" data-testid="heatmap-buckets">
      {BUCKET_KEYS.map(k => {
        const v = buckets[k] || 0;
        const pct = (v / max) * 100;
        // red intensity correlates with drop-off depth (earlier = hotter = more users reached)
        const idx = parseInt(k, 10) / 10;
        const hue = 0; // red family
        const sat = 70 - idx * 4;
        const light = 40 + idx * 3;
        return (
          <div key={k} className="flex-1 flex flex-col items-center justify-end relative group">
            <div style={{ height: `${pct}%`, backgroundColor: `hsl(${hue},${sat}%,${light}%)` }}
                 className="w-full rounded-t-sm transition-all hover:brightness-125" />
            <span className="text-[9px] text-zinc-500 mt-0.5">{k}</span>
            <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 bg-zinc-900 border border-zinc-700 text-[10px] text-zinc-200 px-2 py-0.5 rounded whitespace-nowrap pointer-events-none">
              {k}-{+k + 10}%: <b>{v}</b>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminHeatmap() {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/heatmap?days=${days}&limit=30`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [authFetch, days]);

  useEffect(() => { load(); }, [load]);

  const fmtDwell = (s) => {
    const m = Math.floor(s / 60); const rem = Math.round(s % 60);
    return m ? `${m}m ${rem}s` : `${rem}s`;
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-heatmap-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Activity size={28} className="text-red-500" /> Toplinska mapa čitanja</h1>
          <p className="text-xs text-zinc-400 mt-1">Gdje čitatelji odustaju — po dubini skrolanja članka, u buckets 0-100% po 10%.</p>
        </div>
        <select value={days} onChange={e => setDays(+e.target.value)}
                data-testid="heatmap-days"
                className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm">
          <option value={1}>Zadnji dan</option>
          <option value={7}>Zadnjih 7 dana</option>
          <option value={30}>Zadnjih 30 dana</option>
          <option value={90}>Zadnjih 90 dana</option>
        </select>
      </header>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center text-zinc-500" data-testid="heatmap-empty">
          <p>Još nema prikupljenih podataka o skrolanju za odabrano razdoblje.</p>
          <p className="text-xs mt-2">Heatmap se popunjava čim čitatelji počnu skrolati članke (svaki scroll tick šalje se na <code>/api/public/heatmap/event</code>).</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="heatmap-items">
          {items.map(it => (
            <div key={it.article.id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid={`heatmap-item-${it.article.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-zinc-100 truncate">{it.article.title_hr || '(izbrisan članak)'}</h3>
                  <div className="mt-1.5 grid grid-cols-4 gap-3 text-xs">
                    <div><div className="text-zinc-500 flex items-center gap-1"><Eye size={11}/> Scrolla</div><div className="font-bold text-zinc-100 mt-0.5">{it.total_events.toLocaleString('hr')}</div></div>
                    <div><div className="text-zinc-500 flex items-center gap-1"><Target size={11}/> Posjetitelja</div><div className="font-bold text-zinc-100 mt-0.5">{it.unique_sessions}</div></div>
                    <div><div className="text-zinc-500 flex items-center gap-1"><Clock size={11}/> Prosj. vrijeme</div><div className="font-bold text-zinc-100 mt-0.5">{fmtDwell(it.avg_dwell_seconds)}</div></div>
                    <div><div className="text-zinc-500">Dovršenost</div><div className={`font-bold mt-0.5 ${it.completion_rate > 0.5 ? 'text-emerald-400' : it.completion_rate > 0.25 ? 'text-amber-400' : 'text-red-400'}`}>{Math.round(it.completion_rate * 100)}%</div></div>
                  </div>
                </div>
                <div className="w-64 shrink-0">
                  <BucketsBar buckets={it.buckets} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
